import { createFileRoute, notFound } from "@tanstack/react-router";

import { TravelSlugView } from "@/components/pages/TravelSlugView";
import { meta } from "@/content/meta";
import { pages } from "@/content/pages";
import { isLocale } from "@/i18n/locales";
import { seoHead } from "@/lib/site";
import {
  countriesInRegion,
  countryName,
  fillTemplate,
  generalVaccineNames,
  getCountry,
  isRegionSlug,
} from "@/lib/travel-data";

export const Route = createFileRoute("/$locale/travel/$slug")({
  loader: ({ params }) => {
    if (!isLocale(params.locale)) throw notFound();
    const copy = pages[params.locale].travel;

    if (isRegionSlug(params.slug)) {
      const region = copy.regions.find((item) => item.slug === params.slug);
      if (!region) throw notFound();
      return {
        kind: "region" as const,
        region,
        countries: countriesInRegion(params.slug),
      };
    }

    const country = getCountry(params.slug);
    if (!country) throw notFound();
    const region = country.region
      ? copy.regions.find((item) => item.slug === country.region)
      : undefined;
    const siblings = country.region
      ? countriesInRegion(country.region).filter(
          (item) => item.slug !== country.slug,
        )
      : [];
    return {
      kind: "country" as const,
      country,
      region,
      siblings: siblings.slice(0, 12),
    };
  },
  head: ({ params, loaderData }) => {
    if (!isLocale(params.locale) || !loaderData) return {};
    const templates = meta[params.locale];

    if (loaderData.kind === "region") {
      return seoHead({
        locale: params.locale,
        path: `/travel/${loaderData.region.slug}`,
        title: fillTemplate(templates.travelRegion.title, {
          region: loaderData.region.name,
        }),
        description: fillTemplate(templates.travelRegion.description, {
          region: loaderData.region.name,
        }),
      });
    }

    const name = countryName(loaderData.country, params.locale);
    const vaccines = generalVaccineNames(
      loaderData.country,
      params.locale,
    ).join(", ");
    return seoHead({
      locale: params.locale,
      path: `/travel/${loaderData.country.slug}`,
      title: fillTemplate(templates.travelCountry.title, { country: name }),
      description: fillTemplate(templates.travelCountry.description, {
        country: name,
        vaccines: vaccines || name,
      }),
    });
  },
  component: TravelSlugRoute,
});

function TravelSlugRoute() {
  const { locale } = Route.useParams();
  const data = Route.useLoaderData();
  if (!isLocale(locale)) throw notFound();
  return <TravelSlugView locale={locale} data={data} />;
}
